import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Button, 
    Paper, 
    Alert, 
    CircularProgress,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { uploadMetrics, triggerMetricsCompute } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface MetricStats {
    total: number;
    success: number;
    failed: number;
}

interface UploadMetricsResponse {
    message: string;
    stats: {
        [key: string]: MetricStats | undefined;
    };
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ManageMetrics: React.FC = () => {
    const navigate = useNavigate();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<boolean>(false);
    const [metricsStats, setMetricsStats] = useState<UploadMetricsResponse['stats'] | null>(null);
    const [computeLoading, setComputeLoading] = useState<boolean>(false);
    const [computeError, setComputeError] = useState<string>('');
    const [computeSuccess, setComputeSuccess] = useState<string>('');
    const [clientId, setClientId] = useState<string>('');

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
                file.type !== 'application/vnd.ms-excel') {
                setError('Please upload only Excel files (.xlsx or .xls)');
                return;
            }
            setSelectedFile(file);
            setError('');
            setMetricsStats(null);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file to upload');
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);
        setUploadProgress(true);
        setMetricsStats(null);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await uploadMetrics(formData);
            if (response.data.stats) {
                setMetricsStats(response.data.stats);
            }
            setSuccess(response.data.message || 'Metrics uploaded successfully');
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to upload metrics');
        } finally {
            setLoading(false);
            setUploadProgress(false);
        }
    };

    const processResults = (results: any) => {
        let totalMilestoneCount = 0;
        let totalProjectCount = 0;

        Object.entries(results).forEach(([metricType, metricsObj]) => {
            if (metricsObj && typeof metricsObj === 'object' && !Array.isArray(metricsObj)) {
                const metrics = metricsObj as Record<string, number>;

                if ('milestone_metrics' in metrics) {
                    totalMilestoneCount += Number(metrics.milestone_metrics) || 0;
                } else if ('milestone_variances' in metrics) {
                    totalMilestoneCount += Number(metrics.milestone_variances) || 0;
                }

                if ('project_metrics' in metrics) {
                    totalProjectCount += Number(metrics.project_metrics) || 0;
                } else if ('project_variances' in metrics) {
                    totalProjectCount += Number(metrics.project_variances) || 0;
                }
            }
        });

        setComputeSuccess(`Metrics computation completed successfully! 
            ${totalMilestoneCount} milestone metrics and ${totalProjectCount} project metrics were computed.`);
    };

    const handleTriggerCompute = async () => {
        setComputeLoading(true);
        setComputeError('');
        setComputeSuccess('');
        const newClientId = `client_${Math.random().toString(36).substring(2, 10)}`;
        setClientId(newClientId);

        try {
            const response = await triggerMetricsCompute();
            if (response.data && response.data.results) {
                processResults(response.data.results);
            }
            setComputeSuccess('Metrics computation completed successfully');
        } catch (err: any) {
            setComputeError(err.response?.data?.detail || 'Failed to trigger metrics computation');
        } finally {
            setComputeLoading(false);
        }
    };

    const renderUploadCard = () => (
        <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <UploadIcon sx={{ mr: 1 }} /> Upload Review Entity Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Upload Excel file containing metrics data for review entities.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box sx={{ mt: 2 }}>
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={loading}
                        sx={{ mb: 2 }}
                    >
                        {selectedFile ? selectedFile.name : 'Select Excel File'}
                        <VisuallyHiddenInput type="file" onChange={handleFileChange} accept=".xlsx,.xls" />
                    </Button>
                    {uploadProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography>Processing file, please wait...</Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
            <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={loading || !selectedFile}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                >
                    {loading ? 'Uploading...' : 'Upload'}
                </Button>
            </CardActions>
        </Card>
    );

    const renderComputeCard = () => (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalculateIcon sx={{ mr: 1 }} /> Compute Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Calculate metrics and variances based on the uploaded metric data.
                </Typography>
                {computeLoading && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2">Computing metrics... Please wait.</Typography>
                    </Box>
                )}
                {computeError && <Alert severity="error">{computeError}</Alert>}
                {computeSuccess && <Alert severity="success">{computeSuccess}</Alert>}
            </CardContent>
            <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CalculateIcon />}
                    onClick={handleTriggerCompute}
                    disabled={computeLoading}
                >
                    {computeLoading ? 'Computing...' : 'Compute Metrics'}
                </Button>
            </CardActions>
        </Card>
    );

    const renderThresholdCard = () => (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1 }} /> Threshold Set-up
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    Configure thresholds for various metrics to determine when values should trigger alerts.
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Coming soon...
                </Typography>
            </CardContent>
            <CardActions sx={{ mt: 'auto', pl: 2, pb: 2 }}>
                <Button variant="contained" color="primary" disabled>
                    Configure
                </Button>
            </CardActions>
        </Card>
    );

    return (
        <Box>
            <TitleBar />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4">Manage Metrics</Typography>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate('/view-metrics')}
                    >
                        Back
                    </Button>
                </Box>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>{renderUploadCard()}</Grid>
                    <Grid item xs={12} md={4}>{renderComputeCard()}</Grid>
                    <Grid item xs={12} md={4}>{renderThresholdCard()}</Grid>
                </Grid>

                {metricsStats && (
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Metrics Processing Results
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                                        <TableCell>Dimension</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="right">Success</TableCell>
                                        <TableCell align="right">Failed</TableCell>
                                        <TableCell align="right">Success Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(metricsStats).map(([metricType, stats]) => (
                                        <TableRow key={metricType}>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{metricType}</TableCell>
                                            <TableCell align="right">{stats?.total || 0}</TableCell>
                                            <TableCell align="right">{stats?.success || 0}</TableCell>
                                            <TableCell align="right">{stats?.failed || 0}</TableCell>
                                            <TableCell align="right">
                                                {stats?.total ? `${Math.round((stats.success / stats.total) * 100)}%` : '0%'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default ManageMetrics;
